import { ReactNode } from "react";
import { Link } from "@/lib/router-compat";
import { BadgeCheck } from "lucide-react";
import logoOnDark from "@/assets/logo-on-dark.png";
import lifemartLogo from "@/assets/lp/lifemart-logo.svg";
import { useLifeMart } from "@/hooks/useLifeMart";

/**
 * LifeMartChrome
 * Lightweight co-branded wrapper for tool/page content shown to verified
 * LifeMart members. Renders a slim navy banner with the Home Approach × LifeMart
 * lockup above the page, and a co-branded footer below.
 *
 * Usage:
 *   const { isLifeMartMember } = useLifeMart();
 *   return isLifeMartMember
 *     ? <LifeMartChrome>{toolUI}</LifeMartChrome>
 *     : toolUI;
 */

interface LifeMartChromeProps {
  children: ReactNode;
  /** Optional eyebrow label, defaults to "LifeMart Member Exclusive" */
  label?: string;
}

export const LifeMartChrome = ({
  children,
  label = "LifeMart Member Exclusive",
}: LifeMartChromeProps) => {
  return (
    <>
      <div className="bg-brand text-primary-foreground border-b border-primary-foreground/10">
        <div className="container mx-auto flex flex-wrap items-center justify-between gap-3 px-4 py-2 lg:px-8">
          <div className="flex items-center gap-3 sm:gap-4">
            <Link to="/" aria-label="Home Approach home" className="shrink-0">
              <img src={logoOnDark} alt="Home Approach" className="h-[44px] sm:h-[56px] w-auto" />
            </Link>
            <span aria-hidden className="h-8 w-px bg-primary-foreground/25" />
            <Link to="/lifemart" aria-label="LifeMart" className="shrink-0">
              <span className="inline-flex items-center rounded bg-white/95 px-2 py-1">
                <img src={lifemartLogo} alt="LifeMart" className="h-6 sm:h-7 w-auto" />
              </span>
            </Link>
          </div>
          <p className="inline-flex items-center gap-1.5 text-[10px] font-semibold tracking-[0.22em] uppercase text-[hsl(var(--accent))]">
            <BadgeCheck className="h-3.5 w-3.5" />
            {label}
          </p>
        </div>
      </div>

      {children}

      <div className="border-t border-border bg-card py-4 text-center text-[11px] tracking-wide text-muted-foreground">
        © {new Date().getFullYear()} Home Approach × LifeMart · Member benefit
      </div>
    </>
  );
};

/**
 * Convenience wrapper that auto-applies the chrome when the user is a verified
 * LifeMart member, and falls through to children otherwise. Use this in tool
 * pages so they automatically brand-shift for LifeMart sessions.
 */
export const LifeMartChromeAuto = ({ children, label }: LifeMartChromeProps) => {
  const { isLifeMartMember } = useLifeMart();
  if (!isLifeMartMember) return <>{children}</>;
  return <LifeMartChrome label={label}>{children}</LifeMartChrome>;
};

export default LifeMartChrome;